import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  CreditCard, CheckCircle, AlertCircle, Clock, XCircle,
  ExternalLink, DollarSign, Info, HelpCircle, Loader,
  TrendingUp, Wallet, Calendar, ArrowUpRight, RefreshCw,
  ChevronDown, ChevronUp, Lock, Banknote
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { openExternalUrl } from '../../utils/navigation';
import { formatLongDate } from '../../utils/dateFormatter';
import FeeStatusPill from '../FeeStatusPill';

const PaymentsTab = ({ onboardingStatus, stripeConnecting, handleStripeConnect, API }) => {
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsPeriod, setEarningsPeriod] = useState('all');
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [feeStatus, setFeeStatus] = useState(null);

  const fetchEarnings = async (period = earningsPeriod) => {
    if (!onboardingStatus?.stripe_connected) return;
    setEarningsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/stripe-earnings?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setEarnings(response.data);
    } catch (error) {
      if (error.response?.status !== 403) toast.error('Failed to load earnings data');
    } finally {
      setEarningsLoading(false);
    }
  };

  const fetchSnapshot = async () => {
    if (!onboardingStatus?.stripe_connected) return;
    setSnapshotLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/financial-snapshot`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setSnapshot(response.data);
    } catch (error) {
      console.error('Error fetching financial snapshot:', error);
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    if (onboardingStatus?.stripe_connected && onboardingStatus?.stripe_onboarding_complete) {
      fetchEarnings();
      fetchSnapshot();
    }
  }, [onboardingStatus?.stripe_connected, onboardingStatus?.stripe_onboarding_complete]);

  // Phase 1: vendor-facing platform fee status
  useEffect(() => {
    const fetchFeeStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/vendors/me/fee-status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        setFeeStatus(res.data);
      } catch {
        // Silent — status is informational, non-blocking
      }
    };
    fetchFeeStatus();
  }, [API]);

  const handlePeriodChange = (newPeriod) => {
    setEarningsPeriod(newPeriod);
    fetchEarnings(newPeriod);
  };

  const openStripeDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/stripe-dashboard-link`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      if (response.data.dashboard_url) {
        await openExternalUrl(response.data.dashboard_url);
      } else {
        toast.error('Could not get Stripe dashboard link.');
      }
    } catch {
      toast.error('Failed to open Stripe dashboard.');
    }
  };

  const fmt = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const fmtDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Stripe status helpers
  const getStripeStatus = () => {
    if (!onboardingStatus) return { status: 'loading', label: 'Loading...', color: 'bg-gray-100 text-gray-600' };
    if (onboardingStatus.stripe_is_test_account) return { status: 'needs_attention', label: 'Needs Attention', color: 'bg-red-100 text-red-700', message: 'Your account was created in test mode. Please reconnect with a live Stripe account.' };
    if (onboardingStatus.stripe_connected && onboardingStatus.stripe_onboarding_complete) return { status: 'complete', label: 'Complete', color: 'bg-green-100 text-green-700', message: 'Your Stripe account is fully set up. You can receive payments from customers.' };
    if (onboardingStatus.stripe_needs_setup || (onboardingStatus.stripe_account_id_masked && !onboardingStatus.stripe_onboarding_complete)) return { status: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700', message: 'Your Stripe setup is incomplete. Complete the remaining steps to receive payouts.' };
    return { status: 'not_started', label: 'Not Started', color: 'bg-gray-100 text-gray-600', message: 'Connect your Stripe account to start accepting payments and receiving payouts.' };
  };

  const stripeStatus = getStripeStatus();

  const getStatusIcon = () => {
    switch (stripeStatus.status) {
      case 'complete': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-amber-600" />;
      case 'needs_attention': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'loading': return <Loader className="w-5 h-5 animate-spin text-gray-400" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPrimaryAction = () => {
    if (!onboardingStatus) return null;
    switch (stripeStatus.status) {
      case 'complete':
        return <Button onClick={openStripeDashboard} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="stripe-dashboard-btn"><ExternalLink className="w-4 h-4 mr-2" />View Full Payout Details in Stripe</Button>;
      case 'in_progress':
        return <Button onClick={handleStripeConnect} disabled={stripeConnecting} className="w-full bg-amber-600 hover:bg-amber-700">{stripeConnecting ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : 'Continue Stripe Setup'}</Button>;
      case 'needs_attention':
        return <Button onClick={handleStripeConnect} disabled={stripeConnecting} className="w-full bg-red-600 hover:bg-red-700">{stripeConnecting ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : 'Reconnect Stripe Account'}</Button>;
      default:
        return <Button onClick={handleStripeConnect} disabled={stripeConnecting} className="w-full bg-emerald-600 hover:bg-emerald-700">{stripeConnecting ? <><Loader className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : <><CreditCard className="w-4 h-4 mr-2" />Connect Stripe Account</>}</Button>;
    }
  };

  return (
    <div className="space-y-5" data-testid="payments-tab">
      {/* Standardized FeeStatusPill (card variant) — Payments is the canonical surface, always renders */}
      <FeeStatusPill feeStatus={feeStatus} variant="card" />

      {/* Calm vendor-facing payout reminder — only when this vendor has paid
          bookings (or money sitting in escrow / a failed transfer) AND their
          Stripe onboarding is NOT yet complete. Backend-derived flag from
          /api/vendor/onboarding-status. No modal, no toast, no block. */}
      {onboardingStatus?.has_money_in_motion_without_payouts && !onboardingStatus?.stripe_onboarding_complete && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5"
          data-testid="vendor-payouts-incomplete-banner"
          role="status"
        >
          <AlertCircle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[12px] leading-snug">
            <p className="font-medium text-amber-900">
              Complete payout setup to receive booking payments.
            </p>
            <p className="text-amber-800/90 mt-0.5">
              Your funds are safely held until your Stripe payout setup is finished.
            </p>
          </div>
        </div>
      )}

      {/* Earnings Summary */}
      {stripeStatus.status === 'complete' && (
        <div data-testid="earnings-summary-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <h3 className="text-[15px] font-bold text-gray-900">Earnings</h3>
            </div>
            <div className="flex items-center gap-2">
              <Select value={earningsPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-24 h-7 text-[11px] rounded-full border-gray-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={() => fetchEarnings()} disabled={earningsLoading} className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${earningsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {earningsLoading && !earnings ? (
            <div className="flex items-center justify-center py-8"><Loader className="w-5 h-5 animate-spin text-emerald-600" /></div>
          ) : earnings?.status === 'error' ? (
            <div className="text-center py-4">
              <AlertCircle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-[13px] text-gray-600">{earnings.message}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-white rounded-xl p-3.5 border border-gray-100">
                  <div className="flex items-center gap-1.5 mb-1">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-[11px] text-gray-400 font-medium">Total Earnings</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900" data-testid="total-earnings">{fmt(earnings?.total_earnings || 0)}</p>
                  {earnings?.period_earnings && (
                    <p className="text-[11px] text-emerald-600 mt-0.5">{fmt(earnings.period_earnings.amount)} this {earningsPeriod}</p>
                  )}
                </div>
                <div className="bg-white rounded-xl p-3.5 border border-gray-100">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Wallet className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-[11px] text-gray-400 font-medium">Pending Payout</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900" data-testid="pending-payout">{fmt(earnings?.pending_payout || 0)}</p>
                  {earnings?.available_balance > 0 && (
                    <p className="text-[11px] text-emerald-600 mt-0.5">{fmt(earnings.available_balance)} available</p>
                  )}
                </div>
              </div>

              {earnings?.last_payout_date && (
                <div className="bg-gray-50/80 rounded-xl px-3.5 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-[13px] text-gray-500">Last Payout</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[13px] font-semibold text-gray-900">{fmt(earnings.last_payout_amount)}</span>
                    <span className="text-[11px] text-gray-400 ml-1.5">{fmtDate(earnings.last_payout_date)}</span>
                  </div>
                </div>
              )}

              <Button onClick={openStripeDashboard} variant="outline" className="w-full rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50 text-[13px] h-9" data-testid="stripe-details-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                View Full Details in Stripe
                <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Financial Snapshot */}
      {stripeStatus.status === 'complete' && (
        <div data-testid="financial-snapshot-card">
          <div className="flex items-center gap-2 px-1 mb-2.5">
            <Banknote className="w-4 h-4 text-blue-500" />
            <span className="text-[13px] font-bold text-gray-900">Financial Snapshot</span>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {snapshotLoading ? (
              <div className="flex items-center justify-center py-8"><Loader className="w-5 h-5 animate-spin text-blue-600" /></div>
            ) : (
              <div className="divide-y divide-gray-50">
                {/* Funds pending release */}
                <div className="px-4 py-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                      <Lock className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-gray-800">Pending Release</p>
                      <p className="text-[11px] text-gray-400">Held securely until bookings complete</p>
                    </div>
                  </div>
                  <span className="text-[15px] font-bold text-gray-900" data-testid="funds-pending">{fmt(snapshot?.funds_pending_release || 0)}</span>
                </div>

                {/* Estimated next payout */}
                <div className="px-4 py-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                      <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-gray-800">Next Payout</p>
                      <p className="text-[11px] text-gray-400">
                        {snapshot?.estimated_next_payout ? snapshot.estimated_next_payout.note : 'No upcoming payouts'}
                      </p>
                    </div>
                  </div>
                  {snapshot?.estimated_next_payout ? (
                    <span className="text-[15px] font-bold text-gray-900" data-testid="next-payout-amount">
                      {fmt(snapshot.estimated_next_payout.available_now + snapshot.estimated_next_payout.in_transit)}
                    </span>
                  ) : (
                    <span className="text-[13px] text-gray-300">--</span>
                  )}
                </div>

                {/* Held bookings */}
                {snapshot?.held_bookings && snapshot.held_bookings.length > 0 && (
                  <div className="px-4 py-3">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Held by Booking</p>
                    <div className="space-y-1.5">
                      {snapshot.held_bookings.map((bk) => (
                        <div key={bk.booking_id} className="flex items-center justify-between py-1.5">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-gray-700 truncate">{bk.customer_name}</p>
                            <p className="text-[11px] text-gray-400">{bk.event_type}{bk.event_date ? ` · ${formatLongDate(bk.event_date)}` : ''}</p>
                          </div>
                          <span className="text-[13px] font-semibold text-gray-700 ml-3 shrink-0">{fmt(bk.held_amount)}</span>
                        </div>
                      ))}
                    </div>
                    {snapshot.held_bookings_count > 5 && (
                      <p className="text-[11px] text-gray-400 text-center mt-2">+{snapshot.held_bookings_count - 5} more</p>
                    )}
                  </div>
                )}

                {(!snapshot?.held_bookings || snapshot.held_bookings.length === 0) && snapshot?.funds_pending_release === 0 && (
                  <div className="px-4 py-3">
                    <p className="text-[12px] text-gray-400 text-center">No held funds at this time</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stripe Status Card */}
      <div data-testid="stripe-status-card">
        <div className="flex items-center gap-2 px-1 mb-2.5">
          <CreditCard className="w-4 h-4 text-gray-500" />
          <span className="text-[13px] font-bold text-gray-900">Account Status</span>
          <Badge className={`${stripeStatus.color} text-[10px] ml-auto`} data-testid="stripe-status-badge">{stripeStatus.label}</Badge>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-gray-800">
                  {stripeStatus.status === 'complete' ? 'Ready to Receive Payouts' :
                   stripeStatus.status === 'in_progress' ? 'Setup Incomplete' :
                   stripeStatus.status === 'needs_attention' ? 'Action Required' :
                   'Connect Stripe to Get Started'}
                </p>
                <p className="text-[12px] text-gray-400 mt-0.5">{stripeStatus.message}</p>
              </div>
            </div>

            {onboardingStatus && onboardingStatus.stripe_account_id_masked && (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-50/80 rounded-xl px-3 py-2.5">
                  <p className="text-[10px] text-gray-400 mb-0.5">Payouts</p>
                  <div className="flex items-center gap-1">
                    {onboardingStatus.payouts_enabled ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-gray-400" />}
                    <span className={`text-[11px] font-medium ${onboardingStatus.payouts_enabled ? 'text-emerald-700' : 'text-gray-500'}`}>{onboardingStatus.payouts_enabled ? 'On' : 'Off'}</span>
                  </div>
                </div>
                <div className="bg-gray-50/80 rounded-xl px-3 py-2.5">
                  <p className="text-[10px] text-gray-400 mb-0.5">Charges</p>
                  <div className="flex items-center gap-1">
                    {onboardingStatus.charges_enabled ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-gray-400" />}
                    <span className={`text-[11px] font-medium ${onboardingStatus.charges_enabled ? 'text-emerald-700' : 'text-gray-500'}`}>{onboardingStatus.charges_enabled ? 'On' : 'Off'}</span>
                  </div>
                </div>
                <div className="bg-gray-50/80 rounded-xl px-3 py-2.5">
                  <p className="text-[10px] text-gray-400 mb-0.5">Account</p>
                  <p className="font-mono text-[10px] text-gray-600 truncate" data-testid="stripe-account-id">{onboardingStatus.stripe_account_id_masked}</p>
                </div>
              </div>
            )}

            <div>
              {getPrimaryAction()}
              {stripeStatus.status === 'not_started' && (
                <div className="mt-2.5 space-y-1">
                  <p className="text-[11px] text-gray-400"><span className="font-medium text-gray-500">No business or LLC required.</span> Select "Individual / Sole Proprietor" during setup.</p>
                  <p className="text-[11px] text-gray-400"><span className="font-medium text-gray-500">Secure &amp; private.</span> CeleyApp never sees your bank details.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Facts */}
      <div>
        <div className="flex items-center gap-2 px-1 mb-2.5">
          <Info className="w-4 h-4 text-gray-400" />
          <span className="text-[13px] font-bold text-gray-900">Quick Facts</span>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0"><Clock className="w-3.5 h-3.5 text-emerald-600" /></div>
            <div>
              <p className="text-[13px] font-semibold text-gray-800">Payout Timing</p>
              <p className="text-[11px] text-gray-400">Funds are held during the booking lifecycle and released after completion. First payout: 7-14 days, then 2 business days.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="h-8 w-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0"><AlertCircle className="w-3.5 h-3.5 text-amber-600" /></div>
            <div>
              <p className="text-[13px] font-semibold text-gray-800">$15 Minimum Charge</p>
              <p className="text-[11px] text-gray-400">Stripe requires a minimum of $15 for processing.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0"><ExternalLink className="w-3.5 h-3.5 text-blue-600" /></div>
            <div>
              <p className="text-[13px] font-semibold text-gray-800">Payout Schedule</p>
              <p className="text-[11px] text-gray-400">View and manage schedule in Stripe Dashboard.</p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section — Collapsed */}
      <div data-testid="faq-section">
        <button
          type="button"
          className="w-full flex items-center justify-between px-1 mb-2.5"
          onClick={() => setFaqOpen(prev => !prev)}
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-gray-400" />
            <span className="text-[13px] font-bold text-gray-900">Common Questions</span>
          </div>
          {faqOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {faqOpen && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
            <div className="px-4 py-3">
              <p className="text-[13px] font-semibold text-gray-800 mb-0.5">Why do I see "The KRS Group LLC" in Stripe?</p>
              <p className="text-[11px] text-gray-400 leading-relaxed">CeleyApp is operated by The KRS Group LLC. Your bank details are securely handled by Stripe — we never see them.</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-[13px] font-semibold text-gray-800 mb-0.5">When will I get paid?</p>
              <p className="text-[11px] text-gray-400 leading-relaxed">After a customer pays, funds are held until the booking is completed. Payouts are then processed automatically.</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-[13px] font-semibold text-gray-800 mb-0.5">What if a customer disputes a charge?</p>
              <p className="text-[11px] text-gray-400 leading-relaxed">CeleyApp reviews all disputes. Keep records and communicate through the platform for quick resolution.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentsTab;
